// Testing — the owner's feedback + request surface. Two flows:
//   1. "Give feedback on a feature" → pick a feature, say what's wrong.
//   2. "Request something new"       → a change or a brand-new feature.
// Each submission becomes a ticket (migration 0037) that the team triages on
// the Board. Optional screenshots upload to the private ticket-attachments
// bucket. N1: internal only — nothing here sends or posts anywhere.

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import { MessageSquarePlus, Sparkles, Paperclip, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import GlassCard from '@/components/shared/GlassCard';
import InlineSelect from '@/components/shared/InlineSelect';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import {
  ATTACHMENT_BUCKET,
  FEATURE_AREAS,
  KIND_OPTIONS,
  SEVERITY_OPTIONS,
  fetchTickets,
  kindTone,
  severityTone,
  statusTone,
  type TicketKind,
  type TicketSeverity,
} from '@/lib/tickets';

type Flow = 'feedback' | 'request';

// Matches the bucket's server-side cap (migration 0037); guarded client-side so
// an over-size pick fails fast instead of hanging the Submit button.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
// Strip path segments / unusual chars from a picked file name before it becomes
// part of the storage key (no path traversal, predictable key namespace).
function safeName(name: string): string {
  return (name.split(/[\\/]/).pop() || 'file').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120);
}

const inputClass =
  'w-full rounded-lg neo-inset px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest/30';

export default function Testing() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [flow, setFlow] = useState<Flow>('feedback');
  const [featureArea, setFeatureArea] = useState<string>(FEATURE_AREAS[0]);
  const [kind, setKind] = useState<TicketKind>('bug');
  const [severity, setSeverity] = useState<TicketSeverity>('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  // When the user flips to "Request something new", default the type to a
  // feature request and the area to the catch-all.
  function selectFlow(next: Flow): void {
    setFlow(next);
    if (next === 'request') {
      setKind('feature_request');
      setFeatureArea('Something else / new');
    } else {
      setKind('bug');
      setFeatureArea(FEATURE_AREAS[0]);
    }
  }

  const mine = useQuery({ queryKey: ['tickets', 'all'], queryFn: fetchTickets });

  const submit = useMutation({
    mutationFn: async () => {
      const cleanTitle = title.trim();
      if (!cleanTitle) throw new Error('Add a short title so we know what this is about.');

      const { data: inserted, error } = await supabase
        .from('tickets')
        .insert({
          title: cleanTitle,
          description: description.trim() || null,
          feature_area: featureArea,
          kind,
          severity,
        })
        .select('id')
        .single();
      if (error) throw error;
      const ticketId = (inserted as { id: string }).id;

      // Upload any screenshots, then index them. A failed upload shouldn't lose
      // the ticket — surface it but keep the ticket. The storage key is scoped
      // to the uploader's uid (matches the RLS write policy in migration 0037)
      // and the file name is sanitized (no path traversal).
      const uid = user?.id ?? 'unknown';
      for (const file of files) {
        const path = `${uid}/${ticketId}/${crypto.randomUUID()}-${safeName(file.name)}`;
        const up = await supabase.storage.from(ATTACHMENT_BUCKET).upload(path, file, {
          contentType: file.type || undefined,
          upsert: false,
        });
        if (up.error) {
          toast.error(`Couldn't attach ${file.name}: ${up.error.message}`);
          continue;
        }
        const { error: attErr } = await supabase.from('ticket_attachments').insert({
          ticket_id: ticketId,
          storage_path: path,
          filename: safeName(file.name),
          content_type: file.type || null,
          size_bytes: file.size,
        });
        if (attErr) {
          // The object is in the bucket but the index row failed — don't claim
          // success silently; the file would be unreachable from the drawer.
          toast.error(`Attached ${file.name} but couldn't link it: ${attErr.message}`);
        }
      }
      return ticketId;
    },
    onSuccess: () => {
      toast.success('Thanks — your feedback is logged as a ticket.');
      setTitle('');
      setDescription('');
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Could not submit. Please try again.');
    },
  });

  const recent = useMemo(() => (mine.data ?? []).slice(0, 8), [mine.data]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-5 max-w-5xl">
      {/* ---- Form ---- */}
      <GlassCard>
        {/* Flow toggle */}
        <div className="flex gap-2 mb-5">
          {(
            [
              { id: 'feedback', label: 'Give feedback', icon: MessageSquarePlus },
              { id: 'request', label: 'Request something new', icon: Sparkles },
            ] as const
          ).map((opt) => {
            const active = flow === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => selectFlow(opt.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active ? 'bg-forest-soft text-forest' : 'neo-chip text-foreground/70'
                }`}
              >
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </button>
            );
          })}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit.mutate();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">
                {flow === 'feedback' ? 'Which feature?' : 'Area'}
              </span>
              <InlineSelect
                ariaLabel="Feature area"
                className="mt-1"
                value={featureArea}
                onChange={setFeatureArea}
                options={FEATURE_AREAS.map((f) => ({ value: f, label: f }))}
              />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">Type</span>
              <InlineSelect
                ariaLabel="Type"
                className="mt-1"
                value={kind}
                onChange={(v) => setKind(v as TicketKind)}
                options={KIND_OPTIONS.map((k) => ({
                  value: k.value,
                  label: k.label,
                  hint: k.hint,
                }))}
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Title</span>
            <input
              className={`${inputClass} mt-1`}
              value={title}
              maxLength={200}
              placeholder={
                flow === 'feedback'
                  ? 'e.g. The Inbox draft is cut off'
                  : 'e.g. Weekly time-spend report'
              }
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">
              Details{' '}
              <span className="text-muted-foreground/60">(what happened, what you expected)</span>
            </span>
            <textarea
              className={`${inputClass} mt-1 min-h-[120px] resize-y`}
              value={description}
              placeholder="The more detail the better — steps, what you saw, what you expected instead."
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="flex items-center justify-between gap-4">
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">Severity</span>
              <InlineSelect
                ariaLabel="Severity"
                className="mt-1 w-36"
                value={severity}
                onChange={(v) => setSeverity(v as TicketSeverity)}
                options={SEVERITY_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
              />
            </label>

            <div className="flex-1">
              <span className="text-xs font-medium text-muted-foreground">Screenshots</span>
              <div className="mt-1">
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files ?? []);
                    const tooBig = picked.filter((f) => f.size > MAX_FILE_BYTES);
                    const ok = picked.filter((f) => f.size <= MAX_FILE_BYTES);
                    if (tooBig.length) {
                      toast.error(`${tooBig.map((f) => f.name).join(', ')} over 10 MB — skipped.`);
                    }
                    // Accumulate across picks; the X chips remove individual files.
                    setFiles((prev) => [...prev, ...ok]);
                    // Reset so re-picking the same file fires onChange again.
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                >
                  <Paperclip className="w-4 h-4 mr-1.5" />
                  Attach files
                </Button>
              </div>
            </div>
          </div>

          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span
                  key={`${f.name}-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md neo-chip bg-secondary text-foreground/80"
                >
                  {f.name}
                  <button
                    type="button"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    className="text-muted-foreground hover:text-alert"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="pt-1">
            <Button type="submit" disabled={submit.isPending || !title.trim()}>
              {submit.isPending ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </form>
      </GlassCard>

      {/* ---- Recent submissions ---- */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-forest" />
          Recently submitted
        </h2>
        {mine.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : mine.isError ? (
          <GlassCard>
            <p className="text-sm text-alert">
              Couldn't load your submissions. Refresh to try again.
            </p>
          </GlassCard>
        ) : recent.length === 0 ? (
          <GlassCard variant="inset">
            <p className="text-sm text-muted-foreground">
              Nothing yet. Your submissions show up here and on the Tickets page.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-4">
            {recent.map((t) => {
              const kt = kindTone(t.kind);
              const st = severityTone(t.severity);
              const stt = statusTone(t.status);
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <GlassCard className="!p-3.5">
                    <p className="text-sm font-medium text-foreground leading-snug">{t.title}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${kt.bg} ${kt.text}`}>
                        {kt.label}
                      </span>
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>
                        {st.label}
                      </span>
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${stt.bg} ${stt.text}`}>
                        {stt.label}
                      </span>
                      <span className="text-[11px] text-muted-foreground ml-auto">
                        {formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                      </span>
                    </div>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
